import type {
  AccountId,
  AccountManagerInfo,
  AccountMetainfo,
  ActionMetaInfo,
  ConnectionStatus,
  DefaultContextMenuActionsParams,
  Execution,
  IBrokerConnectionAdapterHost,
  InstrumentInfo,
  IsTradableResult,
  Order,
  OrderTemplate,
  PlaceOrderResult,
  Position,
  PreOrder,
  TradeContext,
} from 'charting_library/broker-api'

const TV_CONNECTION_STATUS_CONNECTED = 1 as ConnectionStatus

export type OrderXTradingOrderIntent = {
  order: OrderTemplate | Order | PreOrder
  source: 'order-dialog' | 'place-order'
}

export type OrderXBrokerBridgeOptions = {
  onOrderIntent?: (intent: OrderXTradingOrderIntent) => void
}

export class OrderXBrokerBridge {
  private readonly host: IBrokerConnectionAdapterHost
  private readonly options: OrderXBrokerBridgeOptions

  constructor(host: IBrokerConnectionAdapterHost, options: OrderXBrokerBridgeOptions = {}) {
    this.host = host
    this.options = options
  }

  connectionStatus(): ConnectionStatus {
    return TV_CONNECTION_STATUS_CONNECTED
  }

  currentAccount(): AccountId {
    return 'orderx' as AccountId
  }

  async accountsMetainfo(): Promise<AccountMetainfo[]> {
    return [{ id: this.currentAccount(), name: 'OrderX' }]
  }

  async isTradable(_symbol: string): Promise<boolean | IsTradableResult> {
    return true
  }

  async symbolInfo(symbol: string): Promise<InstrumentInfo> {
    const minTick = await this.host.getSymbolMinTick(symbol)
    const pipSize = minTick || 1

    return {
      qty: {
        min: 1,
        max: 1e12,
        step: 1,
        default: 1,
      },
      pipValue: pipSize,
      pipSize,
      minTick: minTick || 1,
      description: symbol,
    }
  }

  async orders(): Promise<Order[]> {
    return []
  }

  async positions(): Promise<Position[]> {
    return []
  }

  async executions(_symbol: string): Promise<Execution[]> {
    return []
  }

  async placeOrder(order: PreOrder): Promise<PlaceOrderResult> {
    this.options.onOrderIntent?.({ order, source: 'place-order' })
    return { orderId: `orderx-${Date.now()}` }
  }

  async modifyOrder(_order: Order, _confirmId?: string): Promise<void> {}

  async cancelOrder(_orderId: string): Promise<void> {}

  accountManagerInfo(): AccountManagerInfo {
    return {
      accountTitle: 'OrderX',
      summary: [],
      orderColumns: [],
      positionColumns: [],
      pages: [],
    }
  }

  chartContextMenuActions(
    _context: TradeContext,
    _options?: DefaultContextMenuActionsParams
  ): Promise<ActionMetaInfo[]> {
    // OrderX injects its own Buy/Sell-at-price actions through the chart's
    // context-menu processor. Returning TradingView's defaults as well adds
    // three overlapping broker actions (quick limit, quick stop, Add order),
    // all of which currently route back into the same OrderX Limit form.
    // An empty broker action list hides only that native trading group; chart
    // actions such as Draw Horizontal Line remain owned by TradingView.
    return Promise.resolve([])
  }

  setBuySellButtonsVisible(visible: boolean): void {
    try {
      this.host.sellBuyButtonsVisibility()?.setValue(visible)
    } catch {}
  }
}
